import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_INFO, CONFIG_OPTION_TOAST_NORMAL } from 'common/toast';
import BreadCrumb from 'components/Common/BreadCrumb';
import DropdownOption from 'components/Common/DropdownOption';
import TooltipCustom from 'components/Common/TooltipCustom';
import { useRole } from 'components/Hooks/UserHooks';
import { useFormik } from "formik";
import { AI_MODEL_OPTIONS, SYSTEM_CONFIG_KEY } from 'helpers/constans';
import { isHavePermissionRole, ROLES_FOR_APP } from 'helpers/role';
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from 'react-redux';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Card, CardBody, Col, Container, Input, Label, Modal, ModalBody, ModalHeader, Row, Spinner } from "reactstrap";
import { createSelector } from 'reselect';
import { getConfiguration, getHighlightTimeKeywordConfiguration, putConfiguration, putHighlightTimeKeywordConfiguration } from 'store/thunks';
import * as Yup from "yup";


const Configuration = () => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const AI_MODEL_OPTIONS_LANG: any = AI_MODEL_OPTIONS?.map((item: any) => ({ value: item?.value, label: t(item?.label) }));

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.System;

  const SystemProperties = createSelector(
    selectLayoutState,
    (state) => ({
      configs: state.configs,
      isConfigSuccess: state.isConfigSuccess,
      isConfigLoading: state.isConfigLoading,
      error: state.error,
    })
  );

  // Inside your component
  const { configs } = useSelector(SystemProperties);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    if (isLoading) {
      return;
    }
    setIsOpen((_prev) => true);
  };

  const handleSubmitConfiguration = async (values: any) => {
    try {
      if (isLoading) {
        return;
      }

      setIsLoading((_prev) => true);

      const _data_configs = {
        configs: [
          {
            key: SYSTEM_CONFIG_KEY.AI_NEWS_GENERATION_MODEL,
            value: values?.ai_model?.value ?? '',
            is_active: 1,
          },
          {
            key: SYSTEM_CONFIG_KEY.AI_NEWS_GENERATION_PROMPT,
            value: String(values?.ai_news_generation_prompt ?? ''),
            is_active: 1,
          },
        ]
      };

      const _data_highlight_time_keyword = {
        keywords: values?.highlight_time_keyword?.map((item: any) => (`${item}`)) || [],
      };
      const [res1, res2]: any = await Promise.all([putConfiguration(_data_configs), putHighlightTimeKeywordConfiguration(_data_highlight_time_keyword)]);
      setIsOpen((_prev) => false);
      setIsLoading((_prev) => false);
      if (res1?.code === 200 && res2?.code === 200) {
        toast(`${t('The process has been completed.')}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        toast(`${res1?.message || res1}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsOpen((_prev) => false);
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    ai_model: Yup.object().shape({
      label: Yup.string(),
      value: Yup.string(),
    }).nullable(),
    ai_news_generation_prompt: Yup.string().nullable(),
    highlight_time_keyword: Yup.array().nullable(),
    search_keyword: Yup.string().nullable(),
  });

  const formik = useFormik({
    initialValues: {
      ai_model: AI_MODEL_OPTIONS_LANG[0],
      ai_news_generation_prompt: '',
      highlight_time_keyword: [],
      search_keyword: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleCallAllOption = async () => {
    try {
      const [res1, res2]: any = await Promise.all([getConfiguration({}), getHighlightTimeKeywordConfiguration({})]);
      const _configs = res1?.data?.list || [];
      const _config_ai_model = _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.AI_NEWS_GENERATION_MODEL) || {};
      const _config_ai_news_generation_prompt = _configs?.find((item: any) => item?.key === SYSTEM_CONFIG_KEY.AI_NEWS_GENERATION_PROMPT) || {};
      const _config_highlight_time_keyword = (res2?.data?.list || [])?.map((item: any) => (item?.keyword));
      formik?.setFieldValue('ai_model', AI_MODEL_OPTIONS_LANG?.find((model: any) => model?.value === _config_ai_model?.value));
      formik?.setFieldValue('ai_news_generation_prompt', _config_ai_news_generation_prompt?.value);
      formik?.setFieldValue('highlight_time_keyword', _config_highlight_time_keyword);
      formik?.setFieldValue('search_keyword', '');
    } catch (error: any) {
      return error;
    }
  };

  const handleDeleteHighlightTimeKeyword = (keyword: any) => {
    formik?.setFieldValue('highlight_time_keyword', formik?.values?.highlight_time_keyword?.filter((item: any) => item !== keyword));
  };

  const handleAddHighlightTimeKeyword = () => {
    const _value_highlight_time_keyword: any = formik?.values?.highlight_time_keyword || [];
    const isIncludeSearchKeyword = _value_highlight_time_keyword?.includes(`${formik?.values?.search_keyword || ''}`);
    if (formik?.values?.search_keyword && !isIncludeSearchKeyword) {
      formik?.setFieldValue('highlight_time_keyword', [formik?.values?.search_keyword || '', ..._value_highlight_time_keyword]);
      formik?.setFieldValue('search_keyword', '');
    } else {
      toast(`${t('The keyword is already in the list.')}`, CONFIG_OPTION_TOAST_INFO);
    }
  }

  const listHighlightTimeKeywordAfterSearch = useMemo(() => {
    if (formik?.values?.search_keyword) {
      return formik?.values?.highlight_time_keyword?.filter((item: any) => item?.toLowerCase()?.includes(formik?.values?.search_keyword?.toLowerCase()));
    }
    return formik?.values?.highlight_time_keyword;
  }, [formik?.values?.highlight_time_keyword, formik?.values?.search_keyword]);


  useEffect(() => {
    handleCallAllOption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = `${t('Configuration')} - ${t('AI Article Creation (User)')} | PRESS Ai`;
    document.body.classList.remove('vertical-sidebar-enable');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t('AI Article Creation (User)')} pageTitle={t('Configuration')} />
          <Row>
            {isHavePermissionRole(ROLES_FOR_APP.CONFIGURATION_AI_CUSTOM_NEWS, userPermissions) && (
              <Col lg={12}>
                <Card id="customerList" style={{ boxShadow: '0px 3px 4px 0px rgba(0, 0, 0, 0.03)', minHeight: '75dvh' }}>
                  <CardBody className="pt-3">
                    <form onSubmit={formik.handleSubmit} style={{ maxWidth: '1200px', margin: 'auto' }}>
                      <Row>
                        <Col lg={8} className="px-4 mt-4 mt-md-0">
                          <Row>
                            <div className="mb-3 position-relative pb-4" style={{ border: '1px dashed var(--vz-primary)', borderRadius: '4px', padding: '10px', minHeight: '500px' }}>
                              {/* <span className="text-primary position-absolute fs-13 px-1" style={{ backgroundColor: "var(--vz-secondary-bg)", top: '0px', transform: 'translateY(-50%)' }}>{t('Prompt Area')}</span> */}
                              <Col sm={12} md={12} xl={12} className='mt-2'>
                                <Label htmlFor="ai_model" className="form-label">{t('AI Model')}</Label> <span className="text-danger"> *</span>
                                <DropdownOption
                                  name="ai_model"
                                  dataList={AI_MODEL_OPTIONS_LANG || []}
                                  placeholder={`${t("AI Model")}...`}
                                  className={`dropdown-status-rounded z-hight mb-3 mb-lg-2 mb-xxl-1`}
                                  classNamePrefix="name-prefix"
                                  style={{ width: '100%' }}
                                  initialValue={formik?.values?.ai_model}
                                  onChangeSelect={(e: any) => {
                                    formik.setFieldValue("ai_model", e);
                                  }}
                                />
                              </Col>

                              <Col sm={12} md={12} xl={12} className='mt-2 pb-1'>
                                <Label htmlFor="ai_model" className="form-label">{t('AI Article Generation Prompt')}</Label> <span className="text-danger"> *</span>
                                <Input
                                  className="form-control"
                                  id="ai_news_generation_prompt"
                                  name="ai_news_generation_prompt"
                                  type="textarea"
                                  rows={15}
                                  autoComplete='off'
                                  value={formik?.values?.ai_news_generation_prompt ?? ''}
                                  placeholder={t('AI Article Generation Prompt')}
                                  onChange={(event: any) => {
                                    formik.setFieldValue("ai_news_generation_prompt", event.target.value);
                                  }}
                                />
                                <div className='text-muted position-absolute fs-12 ms-1 mt-1 mb-2'>{('{keyword}')} : {t("When the operator inserts {keyword} into the prompt, it will be replaced with the corresponding keywords separated by commas.")}</div>

                                {formik.touched.ai_news_generation_prompt && formik.errors.ai_news_generation_prompt ? (
                                  <div className="text-danger mt-2">{formik.errors.ai_news_generation_prompt}</div>
                                ) : null}
                              </Col>
                            </div>
                          </Row>
                        </Col>
                        <Col lg={4}>
                          <div className="mb-3 position-relative pb-0" style={{ border: '1px dashed var(--vz-primary)', borderRadius: '4px', padding: '10px' }}>
                            <Row>
                              <Col>
                                <Label htmlFor="highlight_time_keyword" className="form-label">{t('Time Keywords')}</Label> <span className="text-danger"> *</span>
                                <span className="text-primary ms-2">({t('Total')}: {listHighlightTimeKeywordAfterSearch?.length})</span>
                              </Col>
                              <Col sm={12} md={12} xl={12} className='mt-2 pb-1'>
                                <div className="d-flex justify-content-between gap-2">
                                  <Input
                                    className="form-control"
                                    id="search_keyword"
                                    name="search_keyword"
                                    type="text"
                                    value={formik?.values?.search_keyword || ''}
                                    placeholder={t('Search Keyword')}
                                    onChange={(event: any) => {
                                      formik.setFieldValue("search_keyword", event.target.value);
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-secondary fs-14"
                                    onClick={handleAddHighlightTimeKeyword}
                                    disabled={!formik?.values?.search_keyword}
                                    style={{ width: '100px' }}
                                  >
                                    <i className="ri-add-line align-bottom me-1"></i>{" "}
                                    {t('Button Add')}
                                  </button>
                                </div>
                              </Col>
                              <Col className="mt-2 overflow-auto py-3" style={{ height: '400px', background: 'rgb(247, 249, 252)', margin: '0 3px' }}>
                                <div className="d-flex flex-column gap-2">
                                  {
                                    (listHighlightTimeKeywordAfterSearch || [])?.map((item: any, index: number) => (
                                      <div key={index} className="d-flex justify-content-between align-items-center" style={{
                                        border: 'var(--vz-border-width) solid var(--vz-input-border-custom)',
                                        borderRadius: '4px',
                                        padding: '10px',
                                        backgroundColor: 'var(--vz-secondary-bg)',
                                        width: '100%',

                                      }}>
                                        <div>
                                          <span>{item}</span>
                                        </div>
                                        <div>
                                          <button type="button" className="btn btn-sm btn-soft-danger edit-item-btn"
                                            style={{
                                              width: '30px',
                                              height: '30px',
                                            }}
                                            onClick={() => handleDeleteHighlightTimeKeyword(item)}
                                          ><i className="ri-delete-bin-3-fill align-bottom"></i></button>
                                        </div>
                                      </div>
                                    ))
                                  }
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </Col>
                        <Col sm={12} md={12} xl={12} className='mt-1 pt-1 text-end'>
                          <button
                            style={{ width: '150px' }}
                            type="submit"
                            disabled={(isLoading)}
                            className="btn btn-primary fs-14 rounded-pill">
                            {<>{t('Button Update')}</>}
                          </button>
                        </Col>
                      </Row>
                    </form>
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>

        <Modal
          isOpen={!!isOpen}
          centered={true}
          // size="lg"
          toggle={() => setIsOpen(false)}
          keyboard={true}
        >
          <ModalHeader>
            {t('Configuration')}
          </ModalHeader>
          <ModalBody className="">
            <div className="row g-4">
              <Col sm={12}>
                <div className="row g-3">
                  <div className="mt-3 text-center">
                    <h6 className="mb-2 fs-19 text-center mt-1">
                      {t("Are you sure you want to update this?")}
                    </h6>
                    <p className="fs-15 mb-4 d-flex justify-content-center align-items-start align-items-sm-center text-center ">
                      {t('After confirming the update action, the data will be updated. Do you want to proceed with the update action.')}
                    </p>
                    <div className="d-flex gap-3 mt-2 justify-content-center">
                      <button
                        className="btn btn-primary rounded-pill fs-14"
                        type="button"
                        color="light"
                        onClick={() => handleSubmitConfiguration(formik?.values)}
                        style={{ width: '120px' }}
                      >
                        {(isLoading) ? <Spinner size="sm me-2" ></Spinner> : <>{t('Button Update')}</>}
                      </button>
                      <button
                        className="btn btn-secondary rounded-pill fs-14 ms-2"
                        type="button"
                        color="light"
                        onClick={() => setIsOpen(false)}
                        style={{ width: '120px' }}
                      >
                        {t("Button Close")}
                      </button>
                    </div>
                  </div>
                </div>
              </Col>
            </div>
          </ModalBody>
        </Modal >

        <ToastContainer closeButton={false} newestOnTop={false} pauseOnFocusLoss autoClose={2000} limit={2} />

      </div >
    </React.Fragment >
  );
};

export default Configuration;

